import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Eye, EyeOff, Share2, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/AuthService';
import apiClient from '../services/apiClient';
import { getCurrentTrainer, getLoginLogo } from '../services/TrainerAssets';
import { getLanguage, setLanguage, t } from '../i18n';
import ThemeSelector from './ThemeSelector';
import {
  getPwaInstallState,
  requestPwaInstall,
  subscribeToPwaInstall,
} from '../services/PwaInstallService';
import './LoginPage.css';

function validatePassword(value, confirmValue = value, language = getLanguage()) {
  if (value.length < 8) return t('auth.validation.passwordMin', language);
  if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) return t('auth.validation.passwordLetterNumber', language);
  if (value !== confirmValue) return t('auth.validation.passwordMismatch', language);
  return '';
}

function PasswordField({ id, label, value, onChange, disabled, autoComplete, language, showGuidance = false }) {
  const [visible, setVisible] = useState(false);
  const confirmation = id === 'confirmPassword';
  const toggleLabel = t(`authUx.${visible
    ? confirmation ? 'hideConfirmation' : 'hidePassword'
    : confirmation ? 'showConfirmation' : 'showPassword'}`, language);
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div className="login-password-input">
        <input id={id} name={id} type={visible ? 'text' : 'password'} value={value} onChange={onChange}
          disabled={disabled} autoComplete={autoComplete} required
          aria-describedby={showGuidance ? 'login-password-guidance' : undefined} />
        <button type="button" onClick={() => setVisible((current) => !current)} disabled={disabled}
          aria-label={toggleLabel} aria-controls={id} title={toggleLabel}>
          {visible ? <EyeOff size={19} aria-hidden="true" /> : <Eye size={19} aria-hidden="true" />}
        </button>
      </div>
      {showGuidance && <p id="login-password-guidance" className="login-field-hint">{t('authUx.passwordGuidance', language)}</p>}
    </div>
  );
}

function LoginPage() {
  const [form, setForm] = useState({
    email: '',
    nickname: '',
    password: '',
    confirmPassword: '',
    language: getLanguage(),
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [pwaInstallState, setPwaInstallState] = useState(getPwaInstallState);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const installDialogRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentTrainer = getCurrentTrainer();
  const loginLogo = getLoginLogo();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const mode = location.pathname.includes('signup')
    ? 'signup'
    : location.pathname.includes('forgot-password')
      ? 'forgot'
      : location.pathname.includes('reset-password')
        ? 'reset'
        : location.pathname.includes('verify-email')
          ? 'verify'
          : 'login';

  useEffect(() => subscribeToPwaInstall(setPwaInstallState), []);

  useEffect(() => {
    if (!showInstallHelp) return undefined;
    const previousFocus = document.activeElement;
    installDialogRef.current?.querySelector('button')?.focus();
    return () => { if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [showInstallHelp]);

  useEffect(() => {
    if (mode !== 'verify' || !token) return;
    let cancelled = false;
    const verify = async () => {
      setIsLoading(true);
      setError('');
      try {
        await apiClient.verifyEmail(token);
        if (!cancelled) setMessage(t('auth.messages.emailVerified', getLanguage()));
      } catch (err) {
        if (!cancelled) setError(err.message || t('auth.messages.verifyFailed', getLanguage()));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [mode, token]);

  const updateField = (field, value) => {
    if (field === 'language') setLanguage(value);
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
    setMessage('');
  };

  const saveSession = (result, { pendingInitialManual = false } = {}) => {
    const user = result.user || {};
    const session = {
      userId: user.userId,
      email: user.email,
      nickname: user.nickname || user.name || user.email,
      name: user.nickname || user.name || user.email,
      token: result.token,
      language: getLanguage(),
      membershipPlan: user.membershipPlan,
      membershipStatus: user.membershipStatus,
      trialEndsAt: user.trialEndsAt,
      entitlements: user.entitlements,
      createdAt: new Date().toISOString(),
      ...(pendingInitialManual ? { pendingInitialManual: true } : {}),
    };
    authService.setSession(session);
    window.dispatchEvent(new Event('authStateChanged'));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    try {
      if (mode === 'signup') {
        const passwordError = validatePassword(form.password, form.confirmPassword, form.language);
        if (passwordError) throw new Error(passwordError);
        if (!showAgreement) {
          setShowAgreement(true);
          return;
        }
        if (!agreementAccepted) {
          throw new Error(t('auth.validation.agreementRequired', form.language));
        }
        setIsLoading(true);
        const result = await apiClient.signup({
          email: form.email.trim(),
          nickname: form.nickname.trim(),
          password: form.password,
          language: form.language,
        });
        saveSession(result, { pendingInitialManual: true });
        setMessage(t('auth.messages.accountCreated', form.language));
        navigate('/main');
        return;
      }

      setIsLoading(true);
      if (mode === 'forgot') {
        await apiClient.forgotPassword({ email: form.email.trim() });
        setMessage(t('auth.messages.resetSent', form.language));
        return;
      }

      if (mode === 'reset') {
        const passwordError = validatePassword(form.password, form.confirmPassword, form.language);
        if (passwordError) throw new Error(passwordError);
        await apiClient.resetPassword({ token, password: form.password });
        setMessage(t('auth.messages.resetComplete', form.language));
        navigate('/login');
        return;
      }

      const result = await apiClient.login({
        email: form.email.trim(),
        password: form.password,
      });
      saveSession(result);
      navigate('/main');
    } catch (err) {
      setError(err.message || t('auth.messages.failed', form.language));
    } finally {
      setIsLoading(false);
    }
  };

  const heading = t(`auth.headings.${mode}`, form.language);
  const languageButtonLabel = form.language === 'en' ? 'EN' : '\uD55C\uAD6D\uC5B4';
  const isSignupAgreement = mode === 'signup' && showAgreement;
  const submitLabel = isLoading
    ? t('auth.buttons.working', form.language)
    : isSignupAgreement
      ? t('auth.buttons.agreeAndCreate', form.language)
      : heading;
  const installHelpKey = pwaInstallState.isIosSafari
    ? 'pwa.iosSafariInstructions'
    : pwaInstallState.isIos
      ? 'pwa.iosBrowserInstructions'
      : 'pwa.browserInstructions';

  const handleInstall = async () => {
    if (pwaInstallState.canPrompt) {
      try {
        const result = await requestPwaInstall();
        if (result?.outcome !== 'unavailable') return;
      } catch {
        // The browser keeps control of the native prompt. Fall back to manual instructions.
      }
    }
    setShowInstallHelp(true);
  };

  return (
    <div className="login-page">
      <div className="login-page-actions">
        {!pwaInstallState.isStandalone && (
          <button
            type="button"
            className="login-pwa-install-button"
            onClick={handleInstall}
            disabled={isLoading}
          >
            <Download size={16} aria-hidden="true" />
            <span>{t('pwa.installButton', form.language)}</span>
          </button>
        )}
        <button
          type="button"
          className="login-language-toggle"
          onClick={() => updateField('language', form.language === 'en' ? 'ko' : 'en')}
          disabled={isLoading}
          aria-label={t('authUx.changeLanguage', form.language)}
        >
          {languageButtonLabel}
        </button>
      </div>
      <div className="login-hero">
        <div className="login-brand-panel">
          <div className="login-brand-copy">
            <div className="login-wordmark" aria-label={t('app.title.aria', form.language)}>
              <span className="login-wordmark-language">{t('app.title.language', form.language)}</span>
              <span className="login-wordmark-pt">{t('app.title.pt', form.language)}</span>
              <span className="login-wordmark-club">{t('app.title.club', form.language)}</span>
            </div>
            <div className="login-subtitle">{t('app.title.subtitle', form.language)}</div>
            <p className="login-description">{t('authUx.description', form.language)}</p>
          </div>
          <img className="login-character" src={loginLogo} alt={t('authUx.trainer', form.language, { name: currentTrainer.name })} />
        </div>
        <div className="login-overlay">
          <form className="login-form" onSubmit={handleSubmit} aria-labelledby="login-heading" aria-busy={isLoading}>
            <div className="login-form-heading">
              <h1 id="login-heading" className="login-form-heading-line">{heading}</h1>
              <p>{t(`authUx.intro.${mode}`, form.language)}</p>
            </div>

            {isSignupAgreement ? (
              <div className="login-agreement-panel">
                <h2>{t('auth.agreement.title', form.language)}</h2>
                <p>{t('auth.agreement.intro', form.language)}</p>
                <ul className="login-agreement-list">
                  {['0', '1', '2', '3'].map((index) => (
                    <li key={index}>{t(`auth.agreement.terms.${index}`, form.language)}</li>
                  ))}
                </ul>
                <p className="login-agreement-emphasis">{t('auth.agreement.improvement', form.language)}</p>
                <label className="login-agreement-check">
                  <input
                    type="checkbox"
                    checked={agreementAccepted}
                    onChange={(event) => setAgreementAccepted(event.target.checked)}
                    disabled={isLoading}
                  />
                  <span>{t('auth.agreement.checkbox', form.language)}</span>
                </label>
              </div>
            ) : (
              <>
            {mode !== 'reset' && mode !== 'verify' && (
              <div className="form-group">
                <label htmlFor="email">{t('auth.labels.email', form.language)}</label>
                <input
                  id="email"
                  name="email"
                  type={mode === 'login' ? 'text' : 'email'}
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  disabled={isLoading}
                  autoComplete={mode === 'login' ? 'username' : 'email'}
                  required
                />
              </div>
            )}

            {mode === 'signup' && (
              <>
                <div className="form-group">
                  <label htmlFor="nickname">{t('auth.labels.nickname', form.language)}</label>
                  <input id="nickname" name="nickname" autoComplete="nickname" value={form.nickname} onChange={(e) => updateField('nickname', e.target.value)} disabled={isLoading} required />
                </div>
              </>
            )}

            {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
              <PasswordField key={`${mode}-password`} id="password" label={t('auth.labels.password', form.language)}
                value={form.password} onChange={(event) => updateField('password', event.target.value)}
                disabled={isLoading} autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                language={form.language} showGuidance={mode !== 'login'} />
            )}

            {(mode === 'signup' || mode === 'reset') && (
              <PasswordField key={`${mode}-confirmation`} id="confirmPassword" label={t('auth.labels.confirmPassword', form.language)}
                value={form.confirmPassword} onChange={(event) => updateField('confirmPassword', event.target.value)}
                disabled={isLoading} autoComplete="new-password" language={form.language} />
            )}
              </>
            )}

            {message && <div className="login-message" role="status">{message}</div>}
            {error && <div className="login-error" role="alert">{error}</div>}

            {mode !== 'verify' && (
              <div className={isSignupAgreement ? 'login-agreement-actions' : undefined}>
                {isSignupAgreement && (
                  <button
                    type="button"
                    className="login-secondary-button"
                    onClick={() => {
                      setShowAgreement(false);
                      setAgreementAccepted(false);
                    }}
                    disabled={isLoading}
                  >
                    {t('auth.buttons.backToForm', form.language)}
                  </button>
                )}
                <button type="submit" className="login-button" disabled={isLoading}>
                  {submitLabel}
                </button>
              </div>
            )}

            <nav className="login-links" aria-label={t('authUx.help', form.language)}>
              <div className="login-account-link">
                <span>{t(mode === 'login' ? 'authUx.newHere' : 'authUx.alreadyMember', form.language)}</span>
                {mode === 'login'
                  ? <Link to="/signup">{t('auth.links.createAccount', form.language)}</Link>
                  : <Link to="/login">{t('auth.links.signIn', form.language)}</Link>}
              </div>
              {mode === 'login' && <Link className="login-recovery-link" to="/forgot-password">{t('auth.links.forgotPassword', form.language)}</Link>}
            </nav>
            <ThemeSelector language={form.language} disabled={isLoading} />
          </form>
        </div>
      </div>
      {showInstallHelp && (
        <div
          className="login-install-dialog-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowInstallHelp(false);
          }}
        >
          <section
            ref={installDialogRef}
            className="login-install-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-install-dialog-title"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setShowInstallHelp(false);
              } else if (event.key === 'Tab') {
                const buttons = event.currentTarget.querySelectorAll('button:not(:disabled)');
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                  event.preventDefault(); last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                  event.preventDefault(); first?.focus();
                }
              }
            }}
          >
            <button
              type="button"
              className="login-install-dialog-close"
              onClick={() => setShowInstallHelp(false)}
              aria-label={t('common.close', form.language)}
            >
              <X size={20} aria-hidden="true" />
            </button>
            <span className="login-install-dialog-icon" aria-hidden="true">
              <Share2 size={28} />
            </span>
            <h2 id="login-install-dialog-title">{t('pwa.installTitle', form.language)}</h2>
            <p>{t(installHelpKey, form.language)}</p>
            <button
              type="button"
              className="login-install-dialog-done"
              onClick={() => setShowInstallHelp(false)}
            >
              {t('common.close', form.language)}
            </button>
          </section>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
